import './alertPresetGlyph.css';

import type { PresetId } from './alertPresets';

/**
 * The line drawing on a card of "What should be reported?": one thin stroke, rounded ends, a single part
 * that moves on hover. The motion lives in alertPresetGlyph.css and is keyed to the `alert-glyph-host` the
 * glyph sits in, so the whole card is the target, not just the drawing.
 */
const GLYPHS: Record<PresetId, JSX.Element> = {
    deviceAlarm: (
        <>
            <g data-part="bell">
                <path d="M24 12.5V15" />
                <path d="M15 33c2.6-2 3-5.2 3-10a6 6 0 0 1 12 0c0 4.8.4 8 3 10z" />
                <path d="M21.3 36.5a2.7 2.7 0 0 0 5.4 0" />
            </g>
            <path data-part="wave" d="M9.5 21c.7-3.2 2.3-5.7 4.7-7.5" />
            <path data-part="wave" d="M38.5 21c-.7-3.2-2.3-5.7-4.7-7.5" />
        </>
    ),
    pressure: (
        <>
            <path d="M9 34a15 15 0 1 1 30 0" />
            <path d="M9 34h3.5M35.5 34H39M24 19v3.5M13.4 23.4l2.5 2.5M34.6 23.4l-2.5 2.5" />
            <path data-part="needle" d="M24 34V21.5" />
            <circle cx="24" cy="34" r="2.1" />
        </>
    ),
    temperature: (
        <>
            <path d="M20.5 29.6V13a3.5 3.5 0 0 1 7 0v16.6a6.5 6.5 0 1 1-7 0z" />
            <path data-part="mercury" d="M24 31V19" />
            <circle cx="24" cy="35.5" r="2.5" className="fill-current" />
            <path d="M31 14h3M31 19h3M31 24h3" />
        </>
    ),
    battery: (
        <>
            <rect x="7.5" y="17" width="29" height="14" rx="3.5" />
            <path d="M40.5 22v4" />
            <rect data-part="level" x="11.5" y="21" width="5.5" height="6" rx="1.5" className="fill-current" />
        </>
    ),
    noUplink: (
        <>
            <path data-part="arc" data-step="2" d="M9 21a22 22 0 0 1 30 0" />
            <path data-part="arc" data-step="1" d="M14 27a15 15 0 0 1 20 0" />
            <path data-part="arc" data-step="0" d="M19.5 33a7.5 7.5 0 0 1 9 0" />
            <circle cx="24" cy="37.5" r="1.4" className="fill-current" />
            <path data-part="slash" d="M11 10.5 37 40" pathLength={1} strokeDasharray={1} />
        </>
    ),
    custom: (
        <>
            <path d="M9 17h30M9 31h30" />
            <circle data-part="knob-a" cx="17" cy="17" r="3.5" className="fill-background" />
            <circle data-part="knob-b" cx="31" cy="31" r="3.5" className="fill-background" />
        </>
    ),
};

export default function AlertPresetGlyph({ id }: { id: PresetId }) {
    return (
        <svg
            viewBox="0 0 48 48"
            aria-hidden
            focusable="false"
            className="alert-glyph h-full w-full"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            {GLYPHS[id]}
        </svg>
    );
}
